import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useInspectors, type Inspector } from '../../api/inspectors';
import { Button, ButtonGroup, ButtonLink, Input, Pagination, Table } from '../../components/idsk';
import { PageHeading } from '../../layout/PageHeading';
import { usePageTitle } from '../../layout/usePageTitle';
import { useAnnouncer } from '../../layout/Announcer';
import { useListParams } from '../../lib/useListParams';
import { formatPhone } from '../../validation/rules';
import { QueryError } from '../common/QueryError';
import { Loading } from '../common/LoadingPage';
import { RequireAdmin } from '../common/RequireAdmin';

const FILTERS = ['q'] as const;

export function InspectorsListPage() {
  return (
    <RequireAdmin>
      <InspectorsList />
    </RequireAdmin>
  );
}

function InspectorsList() {
  const { t } = useTranslation();
  const { announce } = useAnnouncer();
  const list = useListParams(FILTERS, 'lastName,asc');
  const [q, setQ] = useState(list.filters.q);
  const query = useInspectors({ q: list.filters.q || undefined, page: list.page, size: 20, sort: list.sort });
  usePageTitle([t('inspectors.title')]);
  const labels: Record<string, string> = {
    lastName: t('inspectors.fields.name'),
    email: t('inspectors.fields.email'),
  };
  const apply = (e: FormEvent) => {
    e.preventDefault();
    list.update({ q, page: null });
  };
  return (
    <>
      <div className="govuk-grid-row">
        <div className="govuk-grid-column-two-thirds">
          <PageHeading>{t('inspectors.title')}</PageHeading>
        </div>
        <div className="govuk-grid-column-one-third">
          <ButtonLink to="/inspektori/novy">{t('inspectors.add')}</ButtonLink>
        </div>
      </div>
      <form className="app-filters" role="search" aria-label={t('inspectors.filters.label')} onSubmit={apply} noValidate>
        <Input id="filter-q" label={t('inspectors.filters.q')} hint={t('inspectors.filters.qHint')} value={q} onChange={(e) => setQ(e.target.value)} width="two-thirds" />
        <ButtonGroup>
          <Button type="submit">{t('common.search')}</Button>
          <Button
            variant="secondary"
            onClick={() => {
              setQ('');
              list.update({ q: null, page: null });
            }}
          >
            {t('common.clearFilters')}
          </Button>
        </ButtonGroup>
      </form>
      {query.error ? (
        <QueryError error={query.error} />
      ) : query.isPending ? (
        <Loading />
      ) : (
        <>
          <Table<Inspector>
            caption={t('inspectors.tableCaption')}
            rows={query.data.content}
            rowKey={(i) => i.id}
            sort={list.sortState}
            onSort={(key) => {
              const direction = list.toggleSort(key);
              announce(t('table.sortedBy', { column: labels[key], direction: t(`table.${direction}`) }));
            }}
            columns={[
              {
                key: 'lastName',
                header: labels.lastName,
                sortable: true,
                rowHeader: true,
                render: (i) => (
                  <Link className="govuk-link" to={`/inspektori/${i.id}`}>
                    {i.displayName}
                  </Link>
                ),
              },
              { key: 'email', header: labels.email, sortable: true, render: (i) => i.email },
              { key: 'phone', header: t('inspectors.fields.phone'), render: (i) => formatPhone(i.phone) },
              { key: 'linked', header: t('inspectors.fields.account'), render: (i) => (i.linkedToUser ? t('inspectors.linked') : t('inspectors.notLinked')) },
            ]}
            emptyText={t('inspectors.empty')}
          />
          <Pagination page={query.data.page} totalPages={query.data.totalPages} totalElements={query.data.totalElements} onPage={(p) => list.update({ page: p })} />
        </>
      )}
    </>
  );
}
